import { AppointmentPhase } from "components";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import GroundTransportTypeDropdown from "components/dropdowns/reactselect/GroundTransportTypeDropdown";
import { GoogleMapRoute } from "components/GoogleMap/GoogleMapRoute";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import { SidePanelContactList } from "components/SidePanel/contact/SidePanelContactList";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { GooglePlaceInput } from "components/ValidatedFields/GooglePlaceInput";
import React from "react";
import { getPickupText } from "utilities/enumutils";
import { DriverInnerForm } from "./DriverInnerForm";
import { PickupLocationInput, PickupLocationInputValue } from "./PickupLocationInput";
import { GroundTransportDurationInput } from "./GroundTransportDurationInput";
import { GroundTransportDateTime } from "components/AppointmentDatePicker/GroundTransportDateTime";
import { GroundTransportDuration } from "./GroundTransportDuration";
import { AppointmentDateTimeEnd, AppointmentDateTimeStart } from "components/AppointmentDatePicker/AppointmentDateTime";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import { GooglePlaceState, GroundFieldMetaData, GroundTransportDateTimeState, GroundTransportFormState } from "shared/utils/fieldsHelper/groundFields";

export const groundTransportSections = (
  state: GroundTransportFormState,
  fieldMetaData: GroundFieldMetaData,
  getUpdateGroundTransportInstruction: (gt: GroundTransportFormState) => server.dto.UpdateGroundTransportInstruction,
  hasDriver: boolean,
  hasMap: boolean,
): Array<Section> => [{
  key: "groundTransport",
  type: "regular",
  title: RESX.GroundTransport.resxGroundTransport,
  fields: [{
    ...fieldMetaData.artist.fields(),
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    render: () => <CompanySidePanel type="artist"
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.GroundTransport.Update(
        getUpdateGroundTransportInstruction({ ...state, artist }),
      ),
    },
  }, {
    ...fieldMetaData.travellers.fields(),
    key: "travellers",
    label: RESX.Flight.Travellers,
    render: () => <SidePanelContactList contacts={state.travellers} />,
    inPageEdit: {
      fullWidth: true,
      render: ({ value, onChange, cancel }) => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          inPageEditing={{ cancel }}
          value={value}
          onChange={contacts => onChange(contacts, true)}
          canCreateNew
          required={false}
          validatorKey="contacts"
        />
      </div>,
      initialValue: state.travellers,
      type: "custom",
      submitChange: (travellers: server.dto.ContactSmallV2[]) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({ ...state, travellers }),
      }),
    }
  }, {
    ...fieldMetaData.phase.fields(),
    key: "status",
    label: RESX.Status.resxStatus,
    render: () => <AppointmentPhase value={state.phase.value} displayLabel />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.AppointmentPhase>) => <AppointmentPhaseDropdown
        value={value}
        onChange={(appointmentPhase) => {
          onChange(appointmentPhase, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.phase,
      submitChange: (phase: server.dto.AppointmentPhase) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({ ...state, phase }),
      }),
    },
  }, {
    ...fieldMetaData.groundTransportType.fields(),
    key: "transportType",
    label: RESX.GroundTransport.Transport,
    render: () => <>{state.type.text}</>,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.GroundTransportType>) => <GroundTransportTypeDropdown
        value={value}
        onChange={(v: server.dto.GroundTransportType) => onChange(v, true)}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.type,
      submitChange: (type: server.dto.GroundTransportType) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({ ...state, type }),
      }),
    }
  }, {
    ...fieldMetaData.notes.fields(),
    key: "notes",
    label: RESX.Note.Notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.groundTransport.maxNotesLength}
        rows={3}
        value={value}
        onChange={(title) => { onChange(title); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({ ...state, notes }),
      }),
    },
  }, {
    key: "driver",
    label: RESX.GroundTransport.Driver,
    isEmpty: !hasDriver,
    render: () => <div className="flex-column">
      <div>{state.driver.name}</div>
      <div>{state.driver.mobile}</div>
      <div>{state.driver.email}</div>
      <div>{state.driver.vehicle}</div>
    </div>,
    inPageEdit: {
      initialValue: state.driver,
      fullWidth: true,
      render: ({ value, onChange, submit }) => (
        <DriverInnerForm
          driver={value}
          onChange={onChange}
          submit={submit}
        />
      ),
      type: "custom",
      submitChange: (driver: server.dto.Driver) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({ ...state, driver }),
      }),
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    render: () => <div className="main-section-template-two-column-grid">
      <AppointmentDateTimeStart dateTime={state.appointmentDateTime} />
      <div style={{ paddingLeft: 8 }}>
        <AppointmentDateTimeEnd dateTime={state.appointmentDateTime} />
      </div>
    </div>,
    inPageEdit: {
      render: ({ value, onChange }: RenderEditOptions<GroundTransportDateTimeState>) => <GroundTransportDateTime
        value={value}
        onChange={date => onChange(date)}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.date,
      submitChange: (date: GroundTransportDateTimeState) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({
          ...state,
          date,
        }),
      }),
    }
  }, {
    ...fieldMetaData.durationInMinutes.fields(),
    key: "durationInMinutes",
    label: RESX.Flight.Duration,
    render: () => <GroundTransportDuration
      value={state.durationInMinutes}
      googleEstimatedDurationMinutes={state.directions?.durationEstimateInMinutes}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <GroundTransportDurationInput
        value={value}
        googleEstimatedDurationMinutes={state.directions?.durationEstimateInMinutes}
        onChange={duration => onChange(duration)}
        onEnter={duration => onChange(duration, true)}
        onEsc={() => cancel()}
        autoFocus
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.durationInMinutes,
      submitChange: (durationInMinutes: number) => window.CoreApi.GroundTransport.Update({
        ...getUpdateGroundTransportInstruction({
          ...state,
          durationInMinutes,
        }),
      }),
    }
  }]
}, {
  key: "fromTo",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "fromToLabel",
    label: "",
    left: {
      key: "fromLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "toLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "type",
    label: RESX.GroundTransport.Destination,
    left: {
      ...fieldMetaData.departureType.fields(),
      key: "typeFrom",
      render: () => <>{getPickupText(state.startPickupLocationType, state.startPickupLocationOther)}</>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<PickupLocationInputValue>) => <PickupLocationInput
          value={value}
          focusOnMount
          validatorKey="typeFrom"
          onChange={(v, save) => onChange(v, save)}
          cancel={cancel}
          submit={submit}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: {
          pickupLocationType: state.startPickupLocationType,
          pickupLocationOther: state.startPickupLocationOther,
        },
        submitChange: (value: PickupLocationInputValue) => window.CoreApi.GroundTransport.Update({
          ...getUpdateGroundTransportInstruction({
            ...state,
            startPickupLocationType: value.pickupLocationType,
            startPickupLocationOther: value.pickupLocationOther,
          }),
        }),
      }
    },
    right: {
      ...fieldMetaData.arrivalType.fields(),
      key: "typeTo",
      render: () => <>{getPickupText(state.endPickupLocationType, state.endPickupLocationOther)}</>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<PickupLocationInputValue>) => <PickupLocationInput
          value={value}
          focusOnMount
          validatorKey="typeTo"
          onChange={(v, save) => onChange(v, save)}
          cancel={cancel}
          submit={submit}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: {
          pickupLocationType: state.endPickupLocationType,
          pickupLocationOther: state.endPickupLocationOther,
        },
        submitChange: (value: PickupLocationInputValue) => window.CoreApi.GroundTransport.Update({
          ...getUpdateGroundTransportInstruction({
            ...state,
            endPickupLocationType: value.pickupLocationType,
            endPickupLocationOther: value.pickupLocationOther,
          }),
        }),
      }
    }
  }, {
    key: "address",
    label: RESX.Address.resxAddress,
    left: {
      ...fieldMetaData.departurePlace.fields(),
      key: "addressFrom",
      render: () => <SmallDetails
        label={state.startPlace.name}
        subLabel={state.startPlace.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceState>) => <GooglePlaceInput
          calledFrom="ground"
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.startPlace,
        submitChange: (startPlace: GooglePlaceState) => window.CoreApi.GroundTransport.Update({
          ...getUpdateGroundTransportInstruction({
            ...state,
            startPlace
          }),
        }),
      }
    },
    right: {
      ...fieldMetaData.arrivalPlace.fields(),
      key: "addressTo",
      render: () => <SmallDetails
        label={state.endPlace.name}
        subLabel={state.endPlace.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceState>) => <GooglePlaceInput
          calledFrom="ground"
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.endPlace,
        submitChange: (endPlace: GooglePlaceState) => window.CoreApi.GroundTransport.Update({
          ...getUpdateGroundTransportInstruction({
            ...state,
            endPlace,
          }),
        }),
      }
    }
  }, {
    key: "map",
    label: "",
    hideLabel: true,
    hidden: !hasMap,
    render: () => <GoogleMapRoute
      mapId="DEMO_MAP_ID"
      height={400}
      route={state.directions}
      startPlace={state.startPlace}
      endPlace={state.endPlace}
    />
  }]
}]
